import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { takeUntil, switchMap, catchError, finalize } from 'rxjs/operators';
import { DatabaseService } from '../../services/database/database.service';
import { CodeGenerationRequest, CodeGenerationResponse, DataGenerationRequest, DataGenerationResponse, ToastMessage } from '../../../models/database';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModal, NgbToastModule, NgbTooltipModule  } from '@ng-bootstrap/ng-bootstrap';
import { DataShareService } from '../../services/data_share/data-share.service';
import { ToastService } from '../../services/toast/toast.service';
import { LoadingServiceService } from '../../services/loadingService/loading-service.service';
import { FinalDataDownloadModalComponentComponent } from '../../shared/final-data-download-modal-component/final-data-download-modal-component.component';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';


@Component({
  selector: 'app-codereview-page',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbToastModule, NgbTooltipModule],
  templateUrl: './codereview-page.component.html',
  styleUrl: './codereview-page.component.scss'
})
export class CodereviewPageComponent implements OnInit, OnDestroy {

  pythonScript: string = '';
  aiScript: string = '';
  isEditMode: boolean = false;
  regenerateDisabled: boolean = false;
  generateDataDisabled: boolean = true;
  private destroy$ = new Subject<void>();
  postCodeResponse: CodeGenerationResponse | null = null;
  // toasts: ToastMessage[] = [];
  apiPayload: CodeGenerationRequest | null = null;
  originalPayload: CodeGenerationRequest | null = null;
  dataLoaded: boolean = false;
  isRegenerating: boolean = false;
  scriptName: string = '';
  originalPythonScript: string = '';
  isEdited: boolean = false;
  hasRegenrated: boolean = false;
  generatedData: any[] | null = null;
  autofixEnabled: boolean = false;


  constructor(
    private router: Router,
    private databaseService: DatabaseService,
    private dataSharingService: DataShareService, // Inject the service
    public toastService: ToastService,
    public loadService: LoadingServiceService,
    private modalService: NgbModal,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    @Inject(PLATFORM_ID) private platformId: Object,

  ) { }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadService.show()

      this.dataSharingService.currentCodeGenerationrequest.pipe(
        //finalize(()=> this.loadService.hide()),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (payload) => {
          this.apiPayload = payload;
          // this.originalPayload = this.apiPayload;
          this.dataLoaded = true;
          this.handleCodeResponse(payload);
        },
        error: (error) => {
          this.handleCodeError(error, "Error fetching initial code");
        }
      }
      );

      this.dataSharingService.originalPayload$
      .pipe(takeUntil(this.destroy$))
      .subscribe(payi => {
        this.originalPayload = payi;
      });
    }
  }

  handleCodeResponse(response: any) {
    this.loadService.hide()
    if (response && response.script_content && response.script_name) {
      this.postCodeResponse = response;
      this.scriptName = response.script_name;
      this.pythonScript = response.script_content;
      this.originalPythonScript = response.script_content;
      this.aiScript = response.script_validation;
      if(this.originalPayload && this.apiPayload) {
        this.originalPayload = { ...this.apiPayload }
      }
      if (!this.isRegenerating && !this.toastService.hasShownInitialToast) {
        this.toastService.showToast("Step 3 & 4 Completed Successfully", { classname: 'bg-success text-light' });
        // this.showToast("Script Generated Successfully", { classname: 'bg-success text-light' });
      }
    } else {
      this.toastService.showToast("Script Generation Failed", { classname: 'bg-warning text-light' });
    }
  }

  handleCodeError(error: any, message: string) {
    console.error(message, error);
    this.toastService.showToast(message, { classname: 'bg-danger text-light' });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onAgreeChange(event: any) {
    this.generateDataDisabled = !event.target.checked;
  }

  onAutofixChange(event: any) {
    this.autofixEnabled = event.target.checked;
    // if (this.apiPayload) {
    //   this.apiPayload.autofix = this.autofixEnabled;
    // }
  }

  toggleEditMode() {
    if(this.isEditMode){
      this.isEdited = this.pythonScript !== this.originalPythonScript
      if(this.isEdited){
        this.toastService.showToast("Changes Saved Successfully!", {classname: "bg-success text-light"})
      }
    }else {
      this.originalPythonScript = this.pythonScript
    }
    this.isEditMode = !this.isEditMode;
    this.regenerateDisabled = this.isEditMode;
  }
 

  regenerateCode() {
    if (!this.originalPayload) {
      this.toastService.showToast('Original Payload not available for Re-Generation', {
        classname: 'bg-danger text-light',
      });
      return;
    }
    const updatedPayload: CodeGenerationRequest = {
      ...this.originalPayload,
    };

    console.log("API Payload for regeneration : ", updatedPayload)
    
    this.isRegenerating = true;
    this.regenerateDisabled = true;
    this.loadService.show();

    // this.updatedPayload.autofix = this.autofixEnabled
    //const sessionID = sessionStorage.getItem('Session_Id')
    this.databaseService.generateCode(updatedPayload).pipe( // Using the stored payload
      finalize(() => this.loadService.hide()),
      takeUntil(this.destroy$),
      catchError((error) => {
        this.isRegenerating = false;
        this.regenerateDisabled = false;
        this.handleCodeError(error, "Error during code Re-Generation");
        return of(null);
      })
    ).subscribe({
      next: (response: any) => {
        this.isRegenerating = false;
        this.regenerateDisabled = false;
        if (response && response.script_content) {
          this.pythonScript = response.script_content
          this.aiScript = response.script_validation;

          // this.handleCodeResponse(response);
          const message = this.hasRegenrated ? 'Code Regeneration Successful' : 'Code Re-Generated Successfully'
          const className = this.hasRegenrated ? 'bg-success text-light' : 'bg-success text-light'
          this.toastService.showToast(message, { classname: className })
          this.hasRegenrated = true;
        } else {
          this.toastService.showToast("Code Re-Generation Failes: Invalid Response", { classname: 'bg-warning text-light' })
        }
      },
      error: (error) => {
        this.isRegenerating = false;
        this.regenerateDisabled = false;
        this.handleCodeError(error, "Error during code Re-Generation");
      }
    });
  }

  // Update only the relevant part of PreviewPage method

  PreviewPage() {
    if (!this.generateDataDisabled) {
      const dataGenerationRequest: DataGenerationRequest = {
        script_name: this.scriptName,
        script_content: this.pythonScript,
        edited: this.isEdited,
        autofix: this.autofixEnabled
      }
      this.loadService.show();
      
      this.databaseService.generateData(dataGenerationRequest)
        .pipe(
          finalize(() => this.loadService.hide())
        ).subscribe({
          next: (response: DataGenerationResponse) => {
            if (response.status === 'success') {
              // Open modal and pass complete response
              const modalRef = this.modalService.open(FinalDataDownloadModalComponentComponent, { 
                size: 'lg', 
                centered: true,
                backdrop: 'static',
                keyboard: false
              });
              
              // Get the modal component instance
              const modalComponent = modalRef.componentInstance as FinalDataDownloadModalComponentComponent;
              
              // Set the full response and script details
              modalComponent.setExecutionResponse(response);
              modalComponent.scriptName = this.scriptName;
              modalComponent.scriptContent = this.pythonScript;
              modalComponent.scriptEdited = this.isEdited;
              modalComponent.scriptAutofix = this.autofixEnabled;
              
              this.toastService.showToast(`Data Generation Successful: ${response.status}, Execution Time: ${response.execution_time}`, 
                { classname: "bg-success text-light" });
            } else if (response.status === 'failed') {
              this.toastService.showToast(`Data Generation failed: ${response.error_message}, Execution Time: ${response.execution_time}`, 
                { classname: "bg-danger text-light" });
            } else {
              this.toastService.showToast(`Error Generating Data: ${response.error_message}, Execution Time: ${response.execution_time}`, 
                { classname: "bg-danger text-light" });
            }
          },
          error: (error) => {
            this.handleCodeError(error, "Error Generating Data");
            this.toastService.showToast(`Error Generating Data: ${error.status}`, { classname: "bg-danger text-light" });
          }
        });
      
      this.dataSharingService.UpdateDataGeneratedRequest(dataGenerationRequest);
    }
  }
}
